import { useState, useEffect } from 'react'
import { api, type Project, type Task, type DailySummary } from '../lib/api'
import { hapticFeedback, hapticNotification } from '../lib/telegram'

interface DashboardV2Props {
  category: 'work' | 'personal'
  onSwitchToFocus: () => void
  onSwitchToTasks: () => void
}

export function DashboardV2({ category, onSwitchToFocus, onSwitchToTasks }: DashboardV2Props) {
  const [dailySummary, setDailySummary] = useState<DailySummary | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [dailyFocusTasks, setDailyFocusTasks] = useState<Task[]>([])
  const [allOpenTasks, setAllOpenTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = async () => {
    try {
      setLoading(true)
      const [summaryData, projectsData, focusData, openData] = await Promise.all([
        api.getDailySummary(category),
        api.getProjects(),
        api.getTasks({ status: 'open', category, daily_focus: 'today' }),
        api.getTasks({ status: 'open', category }),
      ])
      setDailySummary(summaryData)
      setProjects(projectsData)
      setDailyFocusTasks(focusData)
      setAllOpenTasks(openData)
    } catch (e) {
      console.error('Failed to fetch dashboard data:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [category])

  const handleCompleteTask = async (taskId: string) => {
    try {
      hapticNotification('success')
      await api.updateTask(taskId, { completed: true })
      await fetchData()
    } catch (e) {
      console.error('Failed to complete task:', e)
    }
  }

  const getProjectProgress = (project: Project) => {
    const total = project.open_tasks + project.done_tasks
    if (total === 0) return 0
    return Math.round((project.done_tasks / total) * 100)
  }

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return '🔴'
      case 'this_week': return '🟡'
      default: return '🔵'
    }
  }

  if (loading) {
    return (
      <div className="px-4 py-8 text-center">
        <div className="spinner" />
        <p className="text-sm text-muted mt-3">Loading dashboard...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Today's Score */}
      <div className="px-4">
        <div className="glass rounded-2xl p-6 bg-gradient-to-r from-green-500/10 to-blue-500/10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">Today's Progress</h2>
            <div className="text-2xl">🎯</div>
          </div>
          <div className="flex items-end gap-6">
            <div>
              <div className="text-3xl font-bold text-green-400">{dailySummary?.completedToday || 0}</div>
              <p className="text-sm text-muted">Tasks completed</p>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">{dailySummary?.urgentTotal || 0}</div>
              <p className="text-sm text-muted">Urgent remaining</p>
            </div>
          </div>
        </div>
      </div>

      {/* Focus Now CTA */}
      <div className="px-4">
        <button
          onClick={() => {
            hapticFeedback('medium')
            onSwitchToFocus()
          }}
          className="w-full glass rounded-2xl p-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/20 active:scale-[0.98] transition-all"
        >
          <div className="flex items-center justify-between">
            <div className="text-left">
              <h3 className="text-lg font-bold mb-1">🎯 Focus Now</h3>
              <p className="text-sm text-muted">Get into the zone with your most important task</p>
            </div>
            <div className="text-2xl">→</div>
          </div>
        </button>
      </div>

      {/* Today's Focus — locked-in tasks (set each morning) */}
      <div className="px-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <span>🔒</span> Today's Focus
          </h2>
          <button 
            onClick={onSwitchToTasks}
            className="text-sm text-accent-light"
          >
            All tasks →
          </button>
        </div>
        <div className="space-y-3">
          {dailyFocusTasks.map((task, index) => (
            <div key={task.id} className="glass rounded-xl p-4 flex items-start gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-accent-light w-4">#{index + 1}</span>
                <button
                  onClick={() => handleCompleteTask(task.id)}
                  className="w-5 h-5 rounded border border-white/30 flex items-center justify-center active:scale-90 transition-all hover:border-green-400"
                />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-white/90">{task.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  {task.project_name && (
                    <span className="text-xs text-muted">
                      {task.project_emoji} {task.project_name}
                    </span>
                  )}
                  {task.subtask_total && task.subtask_total > 0 && (
                    <span className="text-xs text-white/50">
                      {task.subtask_completed || 0}/{task.subtask_total} steps
                    </span>
                  )}
                </div>
              </div>
              <span className="text-sm">{getPriorityIcon(task.priority)}</span>
            </div>
          ))}
          {dailyFocusTasks.length === 0 && (
            <div className="glass rounded-xl p-6 text-center border border-dashed border-white/10">
              <div className="text-3xl mb-2">☀️</div>
              <p className="text-sm text-muted">No tasks locked in for today yet</p>
              <p className="text-xs text-muted mt-1">We'll set your daily focus each morning</p>
            </div>
          )}
        </div>
      </div>

      {/* Project Progress */}
      <div className="px-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <span>📊</span> Project Progress
          </h2>
        </div>
        <div className="space-y-3">
          {projects.slice(0, 4).map((project) => {
            const progress = getProjectProgress(project)
            return (
              <div key={project.id} className="glass rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xl">{project.emoji}</span>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium">{project.name}</h3>
                    <p className="text-xs text-muted">{project.done_tasks} of {project.open_tasks + project.done_tasks} tasks</p>
                  </div>
                  <span className="text-sm font-bold text-accent-light">{progress}%</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full bg-gradient-to-r from-accent to-accent-light transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Team Check-ins (Work only) */}
      {category === 'work' && dailySummary?.teamCheckIns && dailySummary.teamCheckIns.length > 0 && (
        <div className="px-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <span>👥</span> Team Check-ins Needed
            </h2>
          </div>
          <div className="space-y-2">
            {dailySummary.teamCheckIns.map((checkIn, index) => (
              <div key={index} className="glass rounded-xl p-4 bg-orange-500/10 border-l-4 border-orange-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-white/90">{checkIn.assigned_to}</p>
                    <p className="text-xs text-muted">{checkIn.task_count} task{checkIn.task_count > 1 ? 's' : ''} approaching deadline</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-orange-300">Due {new Date(checkIn.earliest_due).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Deadlines */}
      <div className="px-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <span>⏰</span> Upcoming Deadlines
          </h2>
        </div>
        {allOpenTasks.filter((t: Task) => t.due_date).length > 0 ? (
          <div className="space-y-2">
            {allOpenTasks
              .filter((t: Task) => t.due_date)
              .sort((a: Task, b: Task) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime())
              .slice(0, 3)
              .map((task: Task) => {
                const dueDate = new Date(task.due_date!)
                const now = new Date()
                const diffDays = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
                return (
                  <div key={task.id} className="glass rounded-xl p-3 flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white/90">{task.title}</p>
                      <p className="text-xs text-muted">{task.project_emoji} {task.project_name}</p>
                    </div>
                    <div className={`text-xs px-2 py-1 rounded-full ${
                      diffDays < 0 ? 'bg-red-500/20 text-red-300' :
                      diffDays === 0 ? 'bg-yellow-500/20 text-yellow-300' :
                      diffDays <= 3 ? 'bg-orange-500/20 text-orange-300' :
                      'bg-blue-500/20 text-blue-300'
                    }`}>
                      {diffDays < 0 ? 'Overdue' :
                       diffDays === 0 ? 'Today' :
                       diffDays === 1 ? 'Tomorrow' :
                       `${diffDays}d`}
                    </div>
                  </div>
                )
              })}
          </div>
        ) : (
          <div className="glass rounded-xl p-6 text-center">
            <div className="text-3xl mb-2">⏰</div>
            <p className="text-sm text-muted">No upcoming deadlines</p>
          </div>
        )}
      </div>
    </div>
  )
}